/**
 * Billing Transaction – Overall Bills.
 * GET /histopathology/billing/reports/overall/?from_date&to_date&patient_name&bill_number
 *     &corporate_name&created_user&bill_type&payment_status[&export=pdf]
 * Response includes per-bill rows in `data` plus aggregated totals
 * (no_of_bill, total_amount, amount_paid, pending_amount).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Paper, Typography, TextField, IconButton, Tooltip, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DateTime } from "luxon";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import CommonValidation from "../../Utility/CommonValidation";
import { CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import CommonDatePicker from "../../Components/Common Components/CommonDatePicker";
import { CurrencySymbol } from "../../Utility/Constants";
import "../../Styles/Configuration.css";

const today = () => DateTime.now().toFormat("yyyy-MM-dd");

const SummaryCard = ({ label, value }) => (
  <Paper elevation={1} sx={{ px: 2, py: 1.2, minWidth: "10vw", borderRadius: "8px" }}>
    <Typography sx={{ fontSize: "1.2vw", fontWeight: 700, color: "#1a1a1a" }}>{value}</Typography>
    <Typography sx={{ fontSize: "0.9vw", color: "#666" }}>{label}</Typography>
  </Paper>
);

export default function OverallBills() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [dateIndex, setDateIndex] = useState(1);
  const [search, setSearch] = useState("");
  const [totals, setTotals] = useState({ count: 0, total: 0, paid: 0, pending: 0 });
  const [msg, setMsg] = useState({ err: false, errText: "", ok: false, okText: "" });

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (fromDate) params.append("from_date", fromDate);
    if (toDate) params.append("to_date", toDate);
    return params.toString();
  }, [fromDate, toDate]);

  const fetchData = useCallback(() => {
    setLoading(true);
    const url = `${Serviceurls.HISTOPATHOLOGY_BILLING_OVERALL_BILLS}?${buildQuery()}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setRows(res.data.data ?? []);
          setTotals({
            count: res.data.no_of_bill ?? 0,
            total: +res.data.total_amount || 0,
            paid: +res.data.amount_paid || 0,
            pending: +res.data.pending_amount || 0,
          });
        } else {
          setRows([]);
          setTotals({ count: 0, total: 0, paid: 0, pending: 0 });
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        setTotals({ count: 0, total: 0, paid: 0, pending: 0 });
        setMsg({ err: true, errText: err?.response?.data?.message || err?.message, ok: false, okText: "" });
      });
  }, [buildQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      [r.bill_number, r.patient_name, r.nxt_id, r.bill_type, r.status, r.doctor_name, r.corporate_name, r.created_by]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const handleExportPdf = () => {
    const url = `${Serviceurls.HISTOPATHOLOGY_BILLING_OVERALL_BILLS}?${buildQuery()}&export=pdf`;
    RestAPIService.PrintPDF(url)
      .then((res) => {
        const file = new Blob([res.data], { type: "application/pdf" });
        window.open(URL.createObjectURL(file));
      })
      .catch(() => setMsg({ err: true, errText: "Unable to export PDF.", ok: false, okText: "" }));
  };

  const handlePrint = (row) => {
    const billId = row?.id ?? row?.bill_id;
    if (!billId) {
      setMsg({ err: true, errText: "Bill ID missing for print.", ok: false, okText: "" });
      return;
    }
    const url = `${Serviceurls.HISTOPATHOLOGY_BILL_PRINT}?bill_id=${encodeURIComponent(billId)}`;
    RestAPIService.PrintPDF(url)
      .then((res) => {
        const file = new Blob([res.data], { type: "application/pdf" });
        window.open(URL.createObjectURL(file));
      })
      .catch((err) => setMsg({ err: true, errText: err?.response?.data?.message || err?.message || "Unable to print bill.", ok: false, okText: "" }));
  };

  const columns = [
    {
      field: "invoice_date", headerName: "Date And Time", flex: 1.1, minWidth: 160,
      valueGetter: (value, row) =>
        row.invoice_date
          ? row.invoice_date
          : row.created_date ? DateTime.fromISO(row.created_date).toFormat("dd-MM-yyyy hh:mm a") : "-",
    },
    {
      field: "patient_name", headerName: "Patient Name", flex: 1.2, minWidth: 150,
      renderCell: (p) => (
        <Tooltip title={(p.row?.patient_name || "-").toUpperCase()} placement="top" arrow>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              width: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textTransform: "uppercase",
            }}
          >
            {p.row?.patient_name || "-"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "nxt_id", headerName: "NXT ID", flex: 0.9, minWidth: 110,
      valueGetter: (value, row) => row.nxt_id ?? row.uhid ?? "-",
    },
    {
      field: "bill_number", headerName: "Bill Number", flex: 1, minWidth: 130,
      valueGetter: (value, row) => row.bill_number ?? row.bill_no ?? "-",
    },
    {
      field: "bill_type", headerName: "Bill Type", flex: 0.9, minWidth: 110,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2" fontWeight={600}>{p.row.bill_type || "-"}</Typography>
          {p.row.status && p.row.status !== p.row.bill_type?.toLowerCase() && (
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
              {p.row.status}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "payable_amount", headerName: `Bill (${CurrencySymbol})`, flex: 0.8, minWidth: 100,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.payable_amount ?? row.bill_amount ?? 0),
      renderCell: (p) => CommonValidation.AmountFormat(p.row.payable_amount ?? p.row.bill_amount ?? 0, false),
    },
    {
      field: "received_amount", headerName: `Received (${CurrencySymbol})`, flex: 0.8, minWidth: 110,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.received_amount ?? 0),
      renderCell: (p) => CommonValidation.AmountFormat(p.row.received_amount ?? 0, false),
    },
    {
      field: "action", headerName: "Action", sortable: false, flex: 0.6, minWidth: 80,
      align: "center", headerAlign: "center",
      renderCell: (p) => (
        <Tooltip title="Print">
          <IconButton size="small" onClick={() => handlePrint(p.row)}>
            <PrintOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: "0.8vw", height: "100%", display: "flex", flexDirection: "column", gap: "0.8vw", overflow: "hidden", minHeight: 0 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <SummaryCard label="No of Bills" value={totals.count} />
        <SummaryCard label="Total Bill Amount" value={CommonValidation.AmountFormat(totals.total)} />
        <SummaryCard label="Amount Paid" value={CommonValidation.AmountFormat(totals.paid)} />
        <SummaryCard label="Pending Amount" value={CommonValidation.AmountFormat(totals.pending)} />
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ minWidth: "12vw" }}>
          <CommonDatePicker
            FromDate={fromDate}
            ToDate={toDate}
            DateIndex={dateIndex}
            HideAllMenu
            removeMarginRight
            SelectedDates={(from, to, idx) => {
              setFromDate(from);
              setToDate(to);
              setDateIndex(idx);
            }}
          />
        </Box>
        <TextField size="small" placeholder="Search" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: "14vw" }} />
        <Tooltip title="Export PDF">
          <Button variant="outlined" size="small" startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExportPdf} sx={{ textTransform: "capitalize" }}>
            PDF
          </Button>
        </Tooltip>
      </Stack>
      <Paper sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex" }}>
        <DataGrid
          rows={filtered.map((r, i) => ({ ...r, id: r.id ?? r.bill_id ?? `row-${i}` }))}
          columns={columns}
          loading={loading}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
          pageSizeOptions={[10, 20, 30]}
          disableRowSelectionOnClick
          rowHeight={44}
          footerRowHeight={40}
          columnHeaderHeight={42}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => <Stack className="eMed_conf_nodata">No Records Found</Stack>,
          }}
          sx={{
            border: 0,
            flex: 1,
            minWidth: 0,
            "& .MuiDataGrid-scrollbar--horizontal, & .MuiDataGrid-scrollbarFiller--horizontal": {
              display: "none !important",
              height: "0 !important",
              visibility: "hidden !important",
            },
            "& .MuiDataGrid-footerContainer, & .MuiTablePagination-root, & .MuiTablePagination-toolbar": {
              overflow: "hidden !important",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none !important", width: "0 !important", height: "0 !important" },
            },
          }}
        />
      </Paper>
      {msg.err && <ToastMsg severity="error" msg={msg.errText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
      {msg.ok && <ToastMsg severity="success" msg={msg.okText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
    </Box>
  );
}
